import { IdentitySource } from '@infiltre/shared';
import { type Api, createApi } from '@/shared/lib/api';
import type { Identity, IdentityAcquireRequest, IdentityProvider } from './types';

/** Capacitor identity. Bearer in `@capacitor/preferences`. Sync `getToken` via cache. */
const TOKEN_KEY = 'infiltre:token:v1';

interface CapacitorPreferences {
  get(options: { key: string }): Promise<{ value: string | null }>;
  set(options: { key: string; value: string }): Promise<void>;
  remove(options: { key: string }): Promise<void>;
}

let prefsPromise: Promise<CapacitorPreferences> | null = null;

const loadPrefs = async (): Promise<CapacitorPreferences> => {
  // Lazy import — plugin resolves only inside Capacitor WebView.
  const mod = (await import('@capacitor/preferences')) as {
    Preferences: CapacitorPreferences;
  };
  return mod.Preferences;
};

const getPrefs = (): Promise<CapacitorPreferences> => {
  if (!prefsPromise) {
    prefsPromise = loadPrefs().catch((err: unknown) => {
      // Wipe so retries don't hit cached rejection.
      prefsPromise = null;
      const reason = err instanceof Error ? err.message : String(err);
      throw new Error(`identity_failed: capacitor preferences unavailable (${reason})`);
    });
  }
  return prefsPromise;
};

const formatErr = (err: { value: unknown }): string => {
  const v = err.value;
  if (typeof v === 'string') return v;
  if (v && typeof v === 'object' && 'error' in v && typeof v.error === 'string') return v.error;
  return JSON.stringify(v);
};

export const createCapacitorIdentityProvider = (apiBaseUrl: string): IdentityProvider => {
  let cachedToken: string | null = null;
  let api: Api | null = null;

  const getToken = (): string | null => cachedToken;

  const getApi = (): Api => {
    if (!api) api = createApi(apiBaseUrl, getToken);
    return api;
  };

  const persistToken = async (token: string): Promise<void> => {
    cachedToken = token;
    const prefs = await getPrefs();
    await prefs.set({ key: TOKEN_KEY, value: token });
  };

  const dropToken = async (): Promise<void> => {
    cachedToken = null;
    try {
      const prefs = await getPrefs();
      await prefs.remove({ key: TOKEN_KEY });
    } catch {
      // storage unavailable — in-memory clear is sufficient
    }
  };

  const acquire = async (request?: IdentityAcquireRequest): Promise<Identity> => {
    const nickname = request?.nickname?.trim();
    if (!nickname) throw new Error('identity_failed: nickname required for anon login');
    const res = await getApi().auth.anon.post({ name: nickname });
    if (res.error) throw new Error(formatErr(res.error));
    const data = res.data as { token: string; playerId: string; name: string };
    await persistToken(data.token);
    return { playerId: data.playerId, name: data.name, source: IdentitySource.Anon };
  };

  const restore = async (): Promise<Identity | null> => {
    let stored: string | null;
    try {
      const prefs = await getPrefs();
      const { value } = await prefs.get({ key: TOKEN_KEY });
      stored = value ?? null;
    } catch {
      return null;
    }
    if (!stored) return null;
    cachedToken = stored;
    try {
      const res = await getApi().auth.me.get();
      if (res.error) {
        await dropToken();
        return null;
      }
      const data = res.data as { playerId: string; name: string; source: IdentitySource } | null;
      if (!data) {
        await dropToken();
        return null;
      }
      return { playerId: data.playerId, name: data.name, source: data.source };
    } catch {
      await dropToken();
      return null;
    }
  };

  const clear = async (): Promise<void> => {
    await dropToken();
  };

  const setToken = (token: string): void => {
    cachedToken = token;
    void persistToken(token).catch(() => {
      // best-effort persist; cached token still works for the session
    });
  };

  return { acquire, restore, clear, getToken, setToken, source: IdentitySource.Anon };
};
